import React, {createContext, useEffect, useState} from 'react';

import {useViewer} from '../../..//state/viewer/hooks';
import {useAccountList} from '../../../pages/Billing/AccountSettings/AccountPickerSection';
import {AccountType} from '../../../pages/Billing/AccountSettings/util';
import {safeLocalStorage} from '../../../util/localStorage';

export type AccountSelectorValue = {
  name: string;
  id: string;
  accountType: AccountType;
  memberCount?: number;
};

export function safelyParseAccountJson(
  json: string
): AccountSelectorValue | null {
  try {
    return JSON.parse(json);
  } catch (e) {
    return null;
  }
}

export const ACCOUNT_SELECTOR_VALUE = 'account-selector-value';

type AccountSelectorContextProviderProps = {
  selectedAccount: AccountSelectorValue | null;
  setSelectedAccount: (value: AccountSelectorValue) => void;
};

function useDefaultAccount(): AccountSelectorValue | null {
  const viewer = useViewer();
  const accountList = useAccountList();
  const defaultEntityOrganization = viewer?.teams.edges.find(
    org => org.node.id === viewer?.defaultEntity?.id
  );
  const account = accountList.filter(
    o => o.organizationID === defaultEntityOrganization?.node.organizationId
  )?.[0];

  const isPersonalEntity =
    defaultEntityOrganization?.node.name === viewer?.username;

  const defaultAccount = {
    name: account?.name ?? '',
    id: account?.organizationID ?? '',
    accountType: isPersonalEntity ? AccountType.Personal : account?.accountType,
    memberCount: account?.memberCount,
  };

  if (viewer == null || account == null) {
    return null;
  }
  return defaultAccount;
}

export const AccountSelectorContext =
  createContext<AccountSelectorContextProviderProps>({
    selectedAccount: null,
    setSelectedAccount: () => {},
  });

export const AccountSelectorContextProvider: React.FC = ({children}) => {
  const currentlySelectedAccount = safeLocalStorage.getItem(
    ACCOUNT_SELECTOR_VALUE
  );
  const defaultAccount = useDefaultAccount();
  const [selectedAccount, setSelectedAccount] = useState(
    currentlySelectedAccount
      ? safelyParseAccountJson(currentlySelectedAccount)
      : null
  );

  // if viewer hasn't loaded yet and the selected account is still the default account,
  // update the default to the user's default entity organization
  useEffect(() => {
    if (!selectedAccount && defaultAccount) {
      updateSelectedAccount(defaultAccount);
    }
  }, [selectedAccount, defaultAccount]);

  const updateSelectedAccount = (value: AccountSelectorValue) => {
    setSelectedAccount(value);
    const stringValue = JSON.stringify(value);
    safeLocalStorage.setItem(ACCOUNT_SELECTOR_VALUE, stringValue);
  };

  return (
    <AccountSelectorContext.Provider
      value={{
        selectedAccount,
        setSelectedAccount: updateSelectedAccount,
      }}>
      {children}
    </AccountSelectorContext.Provider>
  );
};
